import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import Loader from './components/Loader';
import PageInfo from './components/PageInfo';
import {
    ISearchAttempt,
    SearchAttemptsListResponse,
    formatDateTime,
    truncateText,
    getEngineColor,
    SEARCH_ENGINES,
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS
} from './types/adminDebug';
import './ReportSourcesView.css';

const AdminSearchAttemptsView: React.FC = () => {
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // URL state
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const searchQuery = searchParams.get('search') || '';
    const filterEngine = searchParams.get('engine') || '';
    const filterTargetId = searchParams.get('targetId') || '';
    const fromDate = searchParams.get('fromDate') || '';
    const toDate = searchParams.get('toDate') || '';
    const sortBy = searchParams.get('sortBy') || 'timestamp';
    const sortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc';
    const pageSize = parseInt(searchParams.get('limit') || String(DEFAULT_PAGE_SIZE), 10);

    // Local state
    const [searchAttempts, setSearchAttempts] = useState<ISearchAttempt[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [availableEngines, setAvailableEngines] = useState<string[]>([]);

    const apiBaseUrl = env.apiUrl;
    const apiKey = env.apiKey;

    // Update URL params helper
    const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === '') {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
        });
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    // Fetch filter options
    const fetchFilterOptions = useCallback(async () => {
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/search-attempts/filters/options`, {
                headers
            });

            if (response.ok) {
                const data = await response.json();
                setAvailableEngines(data.engines || SEARCH_ENGINES);
            }
        } catch (err) {
            console.error('Failed to fetch filter options:', err);
            setAvailableEngines([...SEARCH_ENGINES]);
        }
    }, [apiBaseUrl, apiKey, token]);

    // Fetch search attempts
    const fetchSearchAttempts = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: pageSize.toString(),
                sortBy,
                sortOrder
            });

            if (searchQuery) params.append('search', searchQuery);
            if (filterEngine) params.append('engine', filterEngine);
            if (filterTargetId) params.append('targetId', filterTargetId);
            if (fromDate) params.append('fromDate', fromDate);
            if (toDate) params.append('toDate', toDate);

            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/search-attempts?${params}`, {
                headers
            });

            if (!response.ok) {
                const errText = await response.text();
                throw new Error('Failed to fetch search attempts: ' + (response.statusText || errText || 'Unknown error'));
            }

            const data: SearchAttemptsListResponse = await response.json();
            setSearchAttempts(data.searchAttempts);
            setTotalPages(data.pagination.totalPages);
            setTotalItems(data.pagination.total);
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchQuery, filterEngine, filterTargetId, fromDate, toDate, sortBy, sortOrder, token, apiBaseUrl, apiKey]);

    useEffect(() => {
        if (authLoaded) {
            fetchFilterOptions();
        }
    }, [authLoaded, fetchFilterOptions]);

    useEffect(() => {
        if (authLoaded) {
            fetchSearchAttempts();
        }
    }, [authLoaded, fetchSearchAttempts]);

    const handlePageChange = (page: number) => {
        updateSearchParams({ page: page.toString() });
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSearchParams({ search: e.target.value || null, page: '1' });
    };

    const handleEngineChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateSearchParams({ engine: e.target.value || null, page: '1' });
    };

    const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSearchParams({ fromDate: e.target.value || null, page: '1' });
    };

    const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSearchParams({ toDate: e.target.value || null, page: '1' });
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateSearchParams({ limit: e.target.value, page: '1' });
    };

    const handleSort = (column: string) => {
        const newOrder = sortBy === column && sortOrder === 'desc' ? 'asc' : 'desc';
        updateSearchParams({ sortBy: column, sortOrder: newOrder });
    };

    const handleClearFilters = () => {
        updateSearchParams({
            search: null,
            engine: null,
            targetId: null,
            fromDate: null,
            toDate: null,
            sortBy: null,
            sortOrder: null,
            page: '1'
        });
    };

    const handleRowClick = (attempt: ISearchAttempt) => {
        navigate(`/admin/search-attempts/${attempt._id}`);
    };

    const getSortIndicator = (column: string) => {
        if (sortBy !== column) return null;
        return sortOrder === 'asc' ? ' ▲' : ' ▼';
    };

    // Show loading while auth is loading
    if (!authLoaded) {
        return <Loader text={t('common.loading')} />;
    }

    const hasActiveFilters = searchQuery || filterEngine || filterTargetId || fromDate || toDate;

    return (
        <div className="container">
            <h1>{t('adminDebug.searchAttempts.title')}</h1>
            <p className="page-description">{t('adminDebug.searchAttempts.description')}</p>

            {/* Filters */}
            <div className="filters-container" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                    type="text"
                    placeholder={t('adminDebug.searchAttempts.searchPlaceholder')}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ padding: '0.5rem', minWidth: '200px', flex: '1' }}
                />
                <select
                    value={filterEngine}
                    onChange={handleEngineChange}
                    style={{ padding: '0.5rem' }}
                >
                    <option value="">{t('adminDebug.filters.allEngines')}</option>
                    {availableEngines.map(engine => (
                        <option key={engine} value={engine}>{engine}</option>
                    ))}
                </select>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label>{t('adminDebug.filters.from')}:</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={handleFromDateChange}
                        style={{ padding: '0.5rem' }}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label>{t('adminDebug.filters.to')}:</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={handleToDateChange}
                        style={{ padding: '0.5rem' }}
                    />
                </div>
                <select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    style={{ padding: '0.5rem' }}
                >
                    {PAGE_SIZE_OPTIONS.map(size => (
                        <option key={size} value={size}>{size} {t('adminDebug.perPage')}</option>
                    ))}
                </select>
                {hasActiveFilters && (
                    <button
                        onClick={handleClearFilters}
                        style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
                    >
                        {t('adminDebug.filters.clear')}
                    </button>
                )}
            </div>

            {error && (
                <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
                    {error}
                </div>
            )}

            {loading ? (
                <Loader text={t('adminDebug.searchAttempts.loading')} />
            ) : searchAttempts.length > 0 ? (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th
                                    onClick={() => handleSort('_id')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.id')}{getSortIndicator('_id')}
                                </th>
                                <th>{t('adminDebug.fields.searchTargetId')}</th>
                                <th
                                    onClick={() => handleSort('query')}
                                    style={{ cursor: 'pointer', width: '250px' }}
                                >
                                    {t('adminDebug.fields.query')}{getSortIndicator('query')}
                                </th>
                                <th
                                    onClick={() => handleSort('timestamp')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.timestamp')}{getSortIndicator('timestamp')}
                                </th>
                                <th
                                    onClick={() => handleSort('engine')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.engine')}{getSortIndicator('engine')}
                                </th>
                                <th
                                    onClick={() => handleSort('number_of_results')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.numberOfResults')}{getSortIndicator('number_of_results')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {searchAttempts.map(attempt => (
                                <tr
                                    key={attempt._id}
                                    onClick={() => handleRowClick(attempt)}
                                    style={{ cursor: 'pointer' }}
                                    className="clickable-row"
                                >
                                    <td title={attempt._id}>{truncateText(attempt._id, 12)}</td>
                                    <td>
                                        <Link
                                            to={`/admin/search-targets/${attempt.search_target_id}`}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ color: '#007bff' }}
                                            title={attempt.search_target_id}
                                        >
                                            {truncateText(attempt.search_target_id, 12)}
                                        </Link>
                                    </td>
                                    <td title={attempt.query}>{truncateText(attempt.query, 40)}</td>
                                    <td>{formatDateTime(attempt.timestamp)}</td>
                                    <td>
                                        <span
                                            style={{
                                                backgroundColor: getEngineColor(attempt.engine),
                                                color: 'white',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                fontSize: '0.85em'
                                            }}
                                        >
                                            {attempt.engine}
                                        </span>
                                    </td>
                                    <td>{attempt.number_of_results}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Page Info */}
                    <PageInfo
                        count={searchAttempts.length}
                        total={totalItems}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        label={t('adminDebug.searchAttempts.label')}
                    />

                    {/* Pagination Controls */}
                    <div className="pagination-controls">
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage <= 1}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-double-left"></i>
                        </button>

                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage <= 1}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-left"></i>
                        </button>

                        <span className="pagination-page-info">
                            {t('adminDebug.pagination.page')} {currentPage} {t('adminDebug.pagination.of')} {totalPages}
                        </span>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-right"></i>
                        </button>

                        <button
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage >= totalPages}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-double-right"></i>
                        </button>
                    </div>
                </div>
            ) : (
                <div className="no-results">{t('adminDebug.searchAttempts.noResults')}</div>
            )}
        </div>
    );
};

export default AdminSearchAttemptsView;
